// store/index.js
import { createStore } from 'vuex';
import { syncModule } from './syncModule';
import logModule from './modules/logs';
import localforage from 'localforage';
import axios from '@/api/axios';
import { syncService } from '@/services/syncService';

// Initialize localforage instance for tournees
const tourneesStore = localforage.createInstance({
  name: 'tournees'
});

const store = createStore({
  state: {
    // Auth state
    accessToken: localStorage.getItem('accessToken') || null,
    refreshToken: localStorage.getItem('refreshToken') || null,
    role: localStorage.getItem('role') || null,

    // Delivery state
    currentDelivery: null,
    tourneeData: null,
    lastTourneeUpdate: localStorage.getItem('lastTourneeUpdate')
      ? parseInt(localStorage.getItem('lastTourneeUpdate'))
      : null,
    
    // Mode hors ligne explicite (défini par l'utilisateur)
    userDefinedOfflineMode: localStorage.getItem('userDefinedOfflineMode') === 'true' || false
  },

  mutations: {
    // Auth mutations
    setTokens(state, { accessToken, refreshToken }) {
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
    },

    setRole(state, role) {
      state.role = role;
      localStorage.setItem('role', role);
    },

    clearAuthData(state) {
      state.accessToken = null;
      state.refreshToken = null;
      state.role = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('role');
    },

    // Offline mode mutation
    SET_USER_OFFLINE_MODE(state, value) {
      state.userDefinedOfflineMode = value;
      localStorage.setItem('userDefinedOfflineMode', value.toString());
    },

    // Delivery mutations
    SET_CURRENT_DELIVERY(state, delivery) {
      state.currentDelivery = delivery;
    },

    SET_TOURNEE_DATA(state, data) {
      state.tourneeData = data;
    },

    SET_LAST_TOURNEE_UPDATE(state, timestamp) {
      state.lastTourneeUpdate = timestamp;
      localStorage.setItem('lastTourneeUpdate', timestamp.toString());
    },

    UPDATE_CURRENT_DELIVERY(state, updates) {
      if (state.currentDelivery) {
        state.currentDelivery = {
          ...state.currentDelivery,
          ...updates
        };
      }
    },

    CLEAR_DELIVERY_DATA(state) {
      state.currentDelivery = null;
      state.tourneeData = null;
      state.lastTourneeUpdate = null;
      localStorage.removeItem('lastTourneeUpdate');
    },

    UPDATE_DELIVERY_IN_TOURNEE(state, { deliveryId, updates }) {
      if (state.tourneeData?.deliveries) {
        const deliveryIndex = state.tourneeData.deliveries.findIndex(d => d.id === deliveryId);
        if (deliveryIndex !== -1) {
          state.tourneeData.deliveries[deliveryIndex] = {
            ...state.tourneeData.deliveries[deliveryIndex],
            ...updates
          };
        }
      }

      if (state.currentDelivery?.id === deliveryId) {
        state.currentDelivery = {
          ...state.currentDelivery,
          ...updates
        };
      }
    }
  },

  actions: {
    // Auth actions
    login({ commit }, authData) {
      commit('setTokens', {
        accessToken: authData.access,
        refreshToken: authData.refresh,
      });
      commit('setRole', authData.role);
    },

    logout({ commit }) {
      commit('clearAuthData');
      commit('CLEAR_DELIVERY_DATA');
    },

    restoreAuth({ commit }) {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      const role = localStorage.getItem('role');

      if (accessToken && refreshToken && role) {
        commit('setTokens', { accessToken, refreshToken });
        commit('setRole', role);
      }
    },

    // Offline mode
    setUserOfflineMode({ commit }, value) {
      commit('SET_USER_OFFLINE_MODE', value);
    },

    // Delivery actions
    setCurrentDelivery({ commit }, delivery) {
      commit('SET_CURRENT_DELIVERY', delivery);
    },

    async setTourneeData({ commit }, data) {
      if (data?.id) {
        // Utiliser l'identifiant unique 'current_tournee' pour stocker la tournée du jour
        await tourneesStore.setItem('current_tournee', data);
      }
      commit('SET_TOURNEE_DATA', data);
      commit('SET_LAST_TOURNEE_UPDATE', Date.now());
    },

    clearDeliveryData({ commit }) {
      commit('CLEAR_DELIVERY_DATA');
    },

    async loadTourneeData({ commit, state, dispatch, getters }) {
      // Vérifier si l'utilisateur a activé explicitement le mode hors ligne
      if (state.userDefinedOfflineMode) {
        console.log("Mode hors ligne explicite activé par l'utilisateur");
      }
      
      const isOnline = getters['sync/isOnline'] && !state.userDefinedOfflineMode;

      // Si les données sont déjà en mémoire et récentes, les utiliser directement
      if (state.tourneeData && state.lastTourneeUpdate) {
        const cacheAge = Date.now() - state.lastTourneeUpdate;
        const maxAge = 5 * 60 * 1000; // 5 minutes
        if (cacheAge < maxAge) {
          console.log("Utilisation des données en mémoire");
          return true;
        }
      }
      
      let hasLoadedFromCache = false;

      try {
        // Essayer d'abord de charger depuis le cache
        const cachedTournee = await tourneesStore.getItem('current_tournee');

        if (cachedTournee) {
          // Vérifier si la tournée en cache est toujours valide (même date)
          const cachedDate = new Date(cachedTournee.tournee_date).toDateString();
          const today = new Date().toDateString();
          
          if (cachedDate === today) {
            commit('SET_TOURNEE_DATA', cachedTournee);
            commit('SET_LAST_TOURNEE_UPDATE', Date.now());
            hasLoadedFromCache = true;
            console.log("Données chargées depuis le cache local");
          } else {
            console.log("Tournée en cache expirée (date différente)");
          }
        }

        // Si en ligne, essayer de rafraîchir les données
        if (isOnline) {
          try {
            console.log("Tentative de chargement des données depuis le serveur...");
            const response = await axios.get('/livraison/myTournee/');
            const freshData = response.data;

            // Vérifier si les données sont différentes de celles en cache
            const needsUpdate = !cachedTournee ||
              JSON.stringify(cachedTournee) !== JSON.stringify(freshData);

            if (needsUpdate) {
              await dispatch('setTourneeData', freshData);
              console.log("Données actualisées depuis le serveur");
              return true;
            } else {
              console.log("Les données du serveur sont identiques à celles en cache");
            }
          } catch (networkError) {
            console.error('Erreur réseau lors de l\'actualisation:', networkError);
            
            // Ajouter plus d'informations de diagnostic
            console.log(`Statut en ligne: ${navigator.onLine}, Mode hors ligne utilisateur: ${state.userDefinedOfflineMode}`);
            
            if (!hasLoadedFromCache) {
              // Si on n'a pas pu charger depuis le cache, propager l'erreur
              throw networkError;
            } else {
              console.log("Utilisation des données en cache suite à l'erreur réseau");
            }
          }
        } else {
          console.log("Mode hors ligne: utilisation uniquement des données en cache");
        }

        return hasLoadedFromCache;
      } catch (error) {
        console.error('Erreur dans loadTourneeData:', error);
        
        // Enregistrer des informations supplémentaires pour le diagnostic
        if (error.response) {
          console.error('Réponse d\'erreur:', error.response.status, error.response.data);
        } else if (error.request) {
          console.error('Pas de réponse reçue:', error.request);
        } 
        
        throw error;
      }
    },

    // Les actions de synchronisation sont maintenant gérées par le syncModule
    async updateDeliveryStatus({ dispatch }, payload) {
      return dispatch('sync/updateDeliveryWithImages', payload);
    }
  },

  getters: {
    // Auth getters
    isAuthenticated: state => !!state.accessToken,
    getRole: state => state.role,

    // Offline mode getter
    isUserOfflineMode: state => state.userDefinedOfflineMode,

    // Delivery getters
    currentDelivery: state => state.currentDelivery,
    tourneeData: state => state.tourneeData,
    lastTourneeUpdate: state => state.lastTourneeUpdate,

    isTourneeDataCached: state => {
      if (!state.lastTourneeUpdate) return false;
      const cacheAge = Date.now() - state.lastTourneeUpdate;
      const maxAge = 1 * 60 * 1000; // 1 minute
      return cacheAge < maxAge;
    },
    
    // Nouveau getter pour vérifier si la tournée est celle du jour
    isTourneeCurrentDate: state => {
      if (!state.tourneeData?.tournee_date) return false;
      const tourneeDate = new Date(state.tourneeData.tournee_date);
      const today = new Date();
      return tourneeDate.toDateString() === today.toDateString();
    }
  },

  modules: {
    sync: syncModule,
    logs: logModule
  }
});

// Initialize network listeners
store.dispatch('sync/initializeNetworkListeners');

export default store;